import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SchD = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1120 Schedule D for Form 990-T | Reporting Capital Gain"
          description="Learn how nonprofit organizations can attach Form 1120 Schedule D to Form 990-T for reporting capital gains and losses, following accurate filing guidelines."
          keywords=""
          canonical="resources/tax-exempt/schedule-d-form-1120-guide/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 1120{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule D</span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/schedule-d-form-1120-guide/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule D (Form 1120) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Schedule D (Form 1120)
                        </strong>{' '}
                        is used by corporations to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          capital gains and losses
                        </strong>{' '}
                        from sales or exchanges of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital assets
                        </strong>{' '}
                        during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Corporations filing{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1120 </strong>{' '}
                        generally must complete{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule D</strong>{' '}
                        when they have{' '}
                        <strong style={{ fontWeight: 600 }}>
                          capital gains or losses{' '}
                        </strong>
                        that must be reported separately from ordinary business
                        income.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule D (Form 1120) reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          short-term and long-term capital gains and losses,
                        </strong>{' '}
                        including gains or losses from the sale or exchange of
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          stocks, securities, real property, and other capital
                          assets.
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Capital Gain or Loss:
                        </strong>{' '}
                        The information reported on Schedule D is used to
                        determine the corporation's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          net capital gain or loss,
                        </strong>{' '}
                        which is then reflected on{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 1120{' '}
                        </strong>{' '}
                        reported on{' '}
                        <strong style={{ fontWeight: 600 }}>Form 990-T</strong>{' '}
                        as required.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule D (Form 1120)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule D (Form 1120)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schd" sx={{ color: '#0202ff' }}>
                          Schedule D (Form 1120) Filing requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule D (Form
                          1120) filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Schedule D (Form 1120)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule D (Form 1120)
                  </strong>{' '}
                  is a tax form used by corporations to report capital gains and
                  losses. The form requires detailed information about each
                  transaction, including the description of the asset, date of
                  acquisition, date of sale, sales price, and cost or other
                  basis.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Schedule D provides a detailed breakdown of a
                  corporation&apos;s capital gains and losses over the tax year.
                  It helps the IRS determine the corporation&apos;s overall net
                  capital gain or loss, which ultimately impacts their taxable
                  income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Schedule D (Form 1120)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any corporation that has{' '}
                  <strong style={{ fontWeight: 600 }}>
                    capital gains or losses
                  </strong>{' '}
                  during the tax year must file{' '}
                  <strong style={{ fontWeight: 600 }}>
                    Schedule D (Form 1120)
                  </strong>{' '}
                  along with their{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-T return
                  </Link>
                  . This includes:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Corporations that bought and sold capital assets.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Corporations that received distributions from investment
                    funds that report capital gains.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Corporations that experienced involuntary conversions of
                    property (e.g., due to casualty or theft).
                  </li>
                </ul>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 0 }}
                      >
                        Complete your Schedule D (Form 1120) filing requirements
                        and e-file your Form 990-T return with TaxZerone.
                        It&apos;s simple and easy!
                      </Typography>
                      {/* <Typography variant="body2">
                        Make the e-filing process simple and hassle-free by
                        clicking the button below.
                      </Typography> */}
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 260, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120schd`}
                      >
                        Get Started With TaxZerone
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="schd"
                >
                  Schedule D (Form 1120) Filing requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-schd-fund-img.webp" />
                  <source srcSet="/assets/images/f1120-schd-fund-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-schd-fund-img.png"
                    loading="lazy"
                    alt="IRS Schedule D (Form 1120) showing corporation name, EIN, and whether it sold investments in a qualified opportunity fund."
                    width={760}
                    height={145}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Form 1120 Schedule D filing
                  requirements for each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I-Short-term Capital Gains and Losses-Assets Held One
                  Year or Less
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-schd-part1-img.webp" />
                  <source srcSet="/assets/images/f1120-schd-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-schd-part1-img.png"
                    loading="lazy"
                    alt="Form 1120 Schedule D Part I reporting short-term capital gains and losses for assets held one year or less, including sales, basis, and net gain."
                    width={760}
                    height={352}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is the total amount you received when you sold the
                  asset.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is what you originally paid for the asset, including
                  certain fees.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  This includes any corrections or special adjustments from{' '}
                  <Link
                    href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8949
                  </Link>{' '}
                  that change your gain or loss amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  For example, wash sales or other IRS-required changes.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.2 }}>
                  This is your final result.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Formula:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  Column(d)-Column(e) = result{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine the result with Column(g)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter totals for short-term transactions reported on{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1099-B
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-da/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-DA
                  </Link>{' '}
                  where:
                </Typography>
                <Box component="ul" sx={{ mb: 1.2, '& li': { mb: 1.2 } }}>
                  <li>The IRS already received your cost basis, AND</li>
                  <li>You have no adjustments to make. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you report everything on Form 8949 instead, leave this line
                  blank.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}> Form 8949</span> where Box
                  A or Box G is checked. These usually involve transactions
                  where basis was reported to the IRS, but adjustments may
                  apply.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}>Form 8949</span> where Box B
                  or Box H is checked. These are usually transactions where the
                  cost basis was{' '}
                  <span style={{ fontWeight: 600 }}>not reported</span> to the
                  IRS.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter totals from{' '}
                  <span style={{ fontWeight: 600 }}>Form 8949</span> where Box C
                  or Box I is checked. These are transactions that were{' '}
                  <span style={{ fontWeight: 600 }}>
                    not reported on Form 1099-B
                  </span>
                  .{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Short-term gain from installment sales (from Form 6252). If
                  you’re receiving payments over time from a sale, the
                  short-term portion goes here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Short-term gain or loss from like-kind exchanges (from Form
                  8824). If you exchange property and there’s a short-term
                  result, enter it here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you had capital losses from previous years that you
                  couldn’t fully use, enter the unused portion here. This
                  reduces your current year gain.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add everything from lines 1a through 6 (column h).{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Long-Term Capital Gains and Losses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-schd-part2-img.webp" />
                  <source srcSet="/assets/images/f1120-schd-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-schd-part2-img.png"
                    loading="lazy"
                    alt="Form 1120 Schedule D Part II reporting long-term capital gains and losses for assets held over one year, including sales proceeds, basis, and net gain."
                    width={760}
                    height={401}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The columns(d-h) in Part II are the same as the columns
                  explained earlier in Part I, they follow the same meaning and
                  calculation method described above.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this line to report the total of long-term sales shown on
                  Form 1099-B or Form 1099-DA when the cost basis was already
                  reported to the IRS and no adjustments are needed. Instead of
                  listing each transaction on Form 8949, the corporation can
                  combine them and report the totals directly here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This line is used to transfer the total amounts from Form 8949
                  for long-term transactions where Box D or Box J is checked.
                  These transactions were already detailed on Form 8949, so here
                  you simply enter the combined totals.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total of long-term transactions from Form 8949 where
                  Box E or Box K is checked. These usually represent
                  transactions where the cost basis was not reported to the IRS,
                  so they must first be reported on Form 8949 before being
                  summarized here.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this line to report the totals from Form 8949 for
                  transactions with Box F or Box L checked. These are long-term
                  sales that were not reported on a Form 1099, so they must be
                  listed on Form 8949 and then summarized on this line.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the capital gain amount from{' '}
                  <Link
                    href="/resources/tax-exempt/form-4797-sales-business-property/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4797
                  </Link>{' '}
                  (line 7 or line 9). This usually relates to profits from the
                  sale of certain business property, which must be included with
                  long-term capital gains.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report the long-term gain from installment sales taken from
                  Form 6252 (line 26 or line 37). Installment sales are
                  transactions where the payment for the sale is received over
                  time instead of all at once.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the gain or loss from like-kind exchanges reported on
                  Form 8824. A like-kind exchange happens when a corporation
                  trades one business or investment property for another similar
                  property.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total capital gain distributions received from a RIC
                  or REIT during the year. These are profits distributed to the
                  corporation from investment funds, regardless of how long the
                  stock was held.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add together lines 8a through 14 (column h) to calculate the
                  total long-term capital gain or loss for the year. This line
                  shows the overall result after combining all long-term capital
                  transactions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Summary of Parts I and II
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1120-schd-part3-img.webp" />
                  <source srcSet="/assets/images/f1120-schd-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1120-schd-part3-img.png"
                    loading="lazy"
                    alt="Form 1120 Schedule D Part III summarizing short-term and long-term capital gains and losses from Parts I and II, showing total net gain or loss."
                    width={760}
                    height={80}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the corporation has a net short-term capital gain (line 7)
                  and a net long-term capital loss (line 15), enter the amount
                  by which the short-term gain is greater than the long-term
                  loss. This shows the remaining gain after offsetting the loss.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the corporation has a net long-term capital gain (line 15)
                  and a net short-term capital loss (line 7), enter the amount
                  by which the long-term gain exceeds the short-term loss. This
                  represents the corporation’s net capital gain.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 18
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the amounts from lines 16 and 17. Enter the result here
                    and report it on Form 1120, page 1, line 8.
                  </li>
                  <li>
                    If losses are greater than gains, the corporation must
                    follow the capital loss rules in the instructions.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If your
                  total losses are greater than your total gains, refer to the
                  Capital Losses section in the{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/i1120sd.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS instructions
                  </Link>
                  .
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule D (Form 1120)
                  filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone is an IRS-authorized e-file service provider, that
                  offers more than just a seamless 990-T e-filing experience.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When you file with us, you receive instant updates on your
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    990-T
                  </Link>{' '}
                  return&apos;s filing status, ensuring you&apos;re always
                  informed and in the know!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Our user-friendly platform is designed to simplify the
                  e-filing process while providing comprehensive support at
                  every step.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Here&apos;s how your Form 990-T return with Schedule D (Form
                  1120) attachment is transmitted to the IRS in 3 simple steps!
                </Typography>

                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details
                    </strong>{' '}
                    - Choose the tax year for which you want to file the 990-T
                    return, and provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview Schedule D (Form 1120){' '}
                    </strong>{' '}
                    - Attach <span style={{ fontWeight: 600 }}>Schedule D</span>{' '}
                    to report capital gains and losses from sales or exchanges
                    of capital assets and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS
                    </strong>{' '}
                    - Transmit{' '}
                    <span style={{ fontWeight: 600 }}>Schedule D</span> along
                    with your <span style={{ fontWeight: 600 }}>990-T</span>{' '}
                    Return to the IRS and get the acceptance in just a few
                    hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Even if the IRS rejects your{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-T return</span>,
                  there&apos;s no need to worry! TaxZerone has your back with a
                  hassle-free correction and retransmission process,{' '}
                  <span style={{ fontWeight: 600 }}>
                    all at no extra charge!
                  </span>
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 2,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Choose TaxZerone for a filing experience that goes beyond
                    expectations.
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Empower your tax compliance journey with us today!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120schd`}
                  >
                    E-file Now
                  </Button>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f1120sd.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule D (Form 1120) – Capital Gains and Losses
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i1120.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 1120 – U.S. Corporation Income Tax Return
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 88,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What are capital assets?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Capital assets include property such as stocks, bonds, and
                    real estate that a corporation holds for investment
                    purposes, rather than for sale in the ordinary course of
                    business.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How do I determine short-term vs. long-term capital
                  gains/losses?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Assets held for one year or less are considered short-term,
                    whereas assets held for more than one year are long-term.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What happens if a corporation does not file Schedule D when
                  required?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Failing to file Schedule D when required can result in
                    penalties and interest charges from the IRS. It is important
                    to accurately report all capital gains and losses to avoid
                    these penalties.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form1120SchD;
